import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
} from "@mui/material";
import { ShieldX, Home, ArrowLeft, Lock, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../Zustand/Store/useAuthStore";

const NotAllowed = ({
  message = "You don't have permission to access this resource.",
  subMessage = "If you believe this is an error, please contact your administrator.",
  noPermissions = false,
}) => {
  const navigate = useNavigate();
  const { logout } = useAuthStore();

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        background:
          "linear-gradient(135deg, #eff6ff 0%, #eef2ff 50%, #f5f3ff 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 40,
          left: 40,
          width: 80,
          height: 80,
          borderRadius: "50%",
          bgcolor: "rgba(59, 130, 246, 0.15)",
          filter: "blur(24px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 40,
          right: 40,
          width: 120,
          height: 120,
          borderRadius: "50%",
          bgcolor: "rgba(99, 102, 241, 0.15)",
          filter: "blur(32px)",
        }}
      />

      <Paper
        elevation={8}
        sx={{
          maxWidth: 520,
          width: "100%",
          p: { xs: 3, sm: 5 },
          borderRadius: 4,
          textAlign: "center",
          position: "relative",
          zIndex: 1,
          bgcolor: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(8px)",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 88,
            height: 88,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #3b82f6 0%, #4f46e5 100%)",
            mb: 2,
            boxShadow: "0 8px 24px rgba(59, 130, 246, 0.35)",
          }}
        >
          <ShieldX size={44} color="#fff" strokeWidth={2.5} />
        </Box>

        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: "4rem", sm: "5.5rem" },
            fontWeight: 800,
            lineHeight: 1,
            mb: 1,
            background: "linear-gradient(90deg, #2563eb, #4f46e5, #2563eb)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          403
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, color: "#1f2937", mb: 1.5 }}>
          Access Denied
        </Typography>

        <Typography variant="body1" sx={{ color: "#4b5563", mb: 1, px: 1 }}>
          {message}
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b7280", mb: 3, px: 1 }}>
          {subMessage}
        </Typography>

        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3 }}>
          <Lock size={20} color="#93c5fd" />
          <Lock size={20} color="#a5b4fc" />
          <Lock size={20} color="#93c5fd" />
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          justifyContent="center"
        >
          {noPermissions ? (
            <Button
              variant="contained"
              startIcon={<LogOut size={18} />}
              onClick={handleLogout}
              sx={{
                px: 3,
                py: 1.25,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                background: "linear-gradient(90deg, #2563eb, #4f46e5)",
                "&:hover": {
                  background: "linear-gradient(90deg, #1d4ed8, #4338ca)",
                },
              }}
            >
              Logout
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<Home size={18} />}
                onClick={() => navigate("/")}
                sx={{
                  px: 3,
                  py: 1.25,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  background: "linear-gradient(90deg, #2563eb, #4f46e5)",
                  "&:hover": {
                    background: "linear-gradient(90deg, #1d4ed8, #4338ca)",
                  },
                }}
              >
                Go to Home
              </Button>
              <Button
                variant="outlined"
                startIcon={<ArrowLeft size={18} />}
                onClick={() => navigate(-1)}
                sx={{
                  px: 3,
                  py: 1.25,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "#d1d5db",
                  color: "#374151",
                  "&:hover": {
                    borderColor: "#9ca3af",
                    bgcolor: "#f9fafb",
                  },
                }}
              >
                Go Back
              </Button>
            </>
          )}
        </Stack>

        <Typography
          variant="caption"
          sx={{ display: "block", mt: 3, pt: 2, borderTop: "1px solid #e5e7eb", color: "#9ca3af" }}
        >
          Error Code:{" "}
          <Box component="span" sx={{ fontFamily: "monospace", color: "#2563eb", fontWeight: 600 }}>
            ERR_FORBIDDEN
          </Box>
        </Typography>
      </Paper>
    </Box>
  );
};

export default NotAllowed;
